import { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, Share2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { usePlatformFee } from "@/hooks/usePlatformFee";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inr = (v) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v);

const inrIndianUnits = (v) => {
  if (!v || v <= 0) return "₹0";
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} crore`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} lakh`;
  return inr(v);
};

// CGST Rule 32(2)(b): the taxable value on which the bank's 18% GST is levied
// when converting currency. Slabs use the INR-equivalent of the transaction.
// Returns {taxable, gst} in INR.
function bankGstFromInr(inrAmount) {
  let taxable;
  if (inrAmount <= 100_000) {
    taxable = Math.max(0.01 * inrAmount, 250);
  } else if (inrAmount <= 1_000_000) {
    taxable = 1_000 + 0.005 * (inrAmount - 100_000);
  } else {
    taxable = Math.min(5_500 + 0.001 * (inrAmount - 1_000_000), 60_000);
  }
  return { taxable, gst: 0.18 * taxable };
}

/**
 * FX savings widget — shared between Login (unauth) and Dashboard (auth).
 * The /api/fx/usdinr endpoint is public.
 * Variant: "light" (default, on white bg) or "onDark" (on dark hero bg).
 *
 * URL params:
 *   ?vol=<usd-amount>   pre-fills the amount input (public share link)
 *   ?ref=<code>         captured by Login for attribution (not consumed here)
 *
 * Props:
 *   refCode  — when the widget is rendered inside the authed Dashboard, pass the
 *              user's own referral_code so the Share button emits a per-user link.
 */
export default function FxSavingsWidget({ variant = "light", initialAmount = 100000, refCode = null }) {
  // Prefill from ?vol= if present
  const getInitial = () => {
    if (typeof window === "undefined") return initialAmount;
    const p = new URLSearchParams(window.location.search).get("vol");
    const n = Number(p);
    return Number.isFinite(n) && n > 0 ? n : initialAmount;
  };
  const [amount, setAmount] = useState(getInitial);
  const [shipments, setShipments] = useState(12);
  const [fx, setFx] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/fx/usdinr`, { timeout: 10000 })
      .then((r) => setFx(r.data))
      .catch(() => {});
  }, []);

  const mid = fx?.mid_market_rate ?? 87.65;
  const bankRate = fx?.bank_rate ?? mid * 0.98;
  const ourRate = fx?.our_rate ?? mid;
  const usdAmount = Number(amount) || 0;
  const shipmentsPerYear = Math.max(0, Math.round(Number(shipments) || 0));

  // Bank's cost side: FX-margin loss + 18% GST on CGST Rule 32(2)(b) taxable value.
  // The margin is calculated at OUR rate (mid) since that's what the exporter
  // *would* have received. The taxable-value slab uses the INR-equivalent at
  // the bank's rate — that's the amount the bank is converting.
  const rateDifferenceInr = (ourRate - bankRate) * usdAmount;
  const bankInrAmount = usdAmount * bankRate;
  const { taxable: bankGstTaxable, gst: bankGstInr } = bankGstFromInr(bankInrAmount);

  // Flo Payments platform fee (fraction). Single source of truth: /api/platform/fee.
  const FLO_FEE_PCT = usePlatformFee(0.005);
  const floFeeInr = usdAmount * ourRate * FLO_FEE_PCT;

  // Per-shipment savings = bank's total cost (FX margin + GST) − Flo's flat fee.
  const savings = Math.max(0, rateDifferenceInr + bankGstInr - floFeeInr);
  // Annual projection multiplies by the user-entered shipments-per-year value
  // (default 12 = monthly, but fully editable — reflected in the caption too).
  const annualSavings = savings * shipmentsPerYear;

  const validTo = fx?.valid_to || 0;
  const mins =
    validTo > 0 ? Math.max(0, Math.round((Date.now() / 1000 - validTo) / 60)) : null;

  const onDark = variant === "onDark";
  const cardCls = onDark
    ? "rounded-2xl bg-white/95 backdrop-blur border border-white/20 shadow-2xl p-6 md:p-8"
    : "";

  const share = async () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const params = new URLSearchParams();
    params.set("vol", String(Math.round(Number(amount) || 0)));
    if (refCode) params.set("ref", refCode);
    const url = `${origin}/?${params.toString()}`;
    const text = `At $${amount} per shipment × ${shipmentsPerYear}/year, Flo Payments saves me ${inrIndianUnits(annualSavings)} vs my bank. See yours →`;

    try {
      if (navigator.share) {
        await navigator.share({ title: "Flo Payments FX savings", text, url });
        return;
      }
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Share link copied");
    } catch (err) {
      // User cancelled share sheet — silent
      if (err?.name !== "AbortError") toast.error("Could not share link");
    }
  };

  return (
    <div className={cardCls}>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
            What you gain vs. your bank
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-3">
            <div className="flex items-center gap-2">
              <label className="font-display text-sm font-semibold text-muted-foreground" htmlFor="fx-amount">
                Amount per shipment
              </label>
              <span className="font-display text-lg font-bold text-foreground">$</span>
              <input
                id="fx-amount"
                type="number"
                min="1"
                step="any"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-10 w-32 rounded-md border border-input bg-white px-3 text-lg font-display font-bold sf-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="fx-amount-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="font-display text-sm font-semibold text-muted-foreground" htmlFor="fx-shipments">
                Shipments per year
              </label>
              <input
                id="fx-shipments"
                type="number"
                min="1"
                max="365"
                step="1"
                value={shipments}
                onChange={(e) => setShipments(e.target.value)}
                className="h-10 w-20 rounded-md border border-input bg-white px-3 text-lg font-display font-bold sf-mono text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="fx-shipments-input"
              />
            </div>
          </div>
        </div>
        <TrendingUp className="h-5 w-5 text-primary shrink-0" />
      </div>

      <div className="rounded-xl bg-primary/5 border border-primary/20 p-6 mb-6">
        <p className="text-xs uppercase tracking-wider font-medium text-primary">You save per shipment</p>
        <p
          className="font-display text-4xl md:text-5xl font-extrabold text-primary mt-1 sf-mono"
          data-testid="fx-savings"
        >
          {inr(savings)}
        </p>
        <div className="mt-4 pt-4 border-t border-primary/15">
          <p
            className="text-xs uppercase tracking-wider font-medium text-primary"
            data-testid="fx-annual-caption"
          >
            {`Over a year at ${shipmentsPerYear} shipment${shipmentsPerYear === 1 ? "" : "s"}/year`}
          </p>
          <p
            className="font-display text-2xl md:text-3xl font-extrabold text-primary mt-1 sf-mono"
            data-testid="fx-annual-savings"
          >
            ≈ {inrIndianUnits(annualSavings)}
          </p>
        </div>

        <button
          type="button"
          onClick={share}
          className="mt-5 inline-flex items-center gap-2 text-xs font-semibold text-primary hover:underline"
          data-testid="fx-share-btn"
        >
          <Share2 className="h-3.5 w-3.5" />
          Share this calculation
        </button>
      </div>

      <div className="space-y-3 text-sm">
        <Row label="Your bank's typical rate" value={`₹${bankRate.toFixed(2)} / $`} tone="muted" />
        <Row
          label="Flo Payments rate"
          value={`₹${ourRate.toFixed(2)} / $`}
          tone="primary"
          sublabel="(Google mid-market rate — no markup)"
        />
        <p className="text-xs text-muted-foreground -mt-1">
          ~2% better, plus we charge no GST.
        </p>

        <button
          type="button"
          onClick={() => setShowBreakdown((s) => !s)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
          data-testid="fx-breakdown-toggle"
          aria-expanded={showBreakdown}
        >
          {showBreakdown ? (
            <>
              <ChevronUp className="h-3.5 w-3.5" /> Hide calculation
            </>
          ) : (
            <>
              <ChevronDown className="h-3.5 w-3.5" /> See how we calculated this
            </>
          )}
        </button>

        {showBreakdown && (
          <div
            className="mt-3 rounded-lg border border-border/60 bg-muted/40 p-4 space-y-2 text-xs"
            data-testid="fx-breakdown"
          >
            <BreakdownRow label="Google mid-market rate" value={`₹${mid.toFixed(4)} / $`} />
            <BreakdownRow
              label="Your bank's rate (~2% below mid-market)"
              value={`₹${bankRate.toFixed(4)} / $`}
            />
            <BreakdownRow
              label={`Rate difference on $${usdAmount.toLocaleString("en-IN")}`}
              value={`+ ${inr(rateDifferenceInr)}`}
              tone="gain"
            />
            <BreakdownRow
              label={`Bank's 18% GST on conversion (₹${bankGstTaxable.toLocaleString("en-IN", { maximumFractionDigits: 0 })} taxable value)`}
              value={`+ ${inr(bankGstInr)}`}
              tone="gain"
            />
            <BreakdownRow
              label={`Flo Payments fee (${+((FLO_FEE_PCT * 100).toFixed(3))}%, no GST)`}
              value={`− ${inr(floFeeInr)}`}
              tone="cost"
            />
            <div className="pt-2 mt-2 border-t border-border/60 flex justify-between items-center">
              <span className="font-semibold text-foreground">= You save</span>
              <span className="font-bold sf-mono text-primary">{inr(savings)}</span>
            </div>
          </div>
        )}

        <div className="pt-3 border-t text-xs text-muted-foreground">
          Google mid-market rate
          {mins !== null && ` · Updated ${mins} min${mins === 1 ? "" : "s"} ago`}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, tone, sublabel }) {
  return (
    <div className="flex justify-between items-start gap-2">
      <div className="min-w-0">
        <span className="text-muted-foreground">{label}</span>
      </div>
      <div className="text-right shrink-0">
        <span className={`font-semibold sf-mono ${tone === "primary" ? "text-primary" : "text-muted-foreground"}`}>
          {value}
        </span>
        {sublabel && (
          <div className="text-[10px] text-muted-foreground/80 mt-0.5 font-medium">
            {sublabel}
          </div>
        )}
      </div>
    </div>
  );
}

function BreakdownRow({ label, value, tone }) {
  const cls =
    tone === "gain"
      ? "text-[#1E8A5B]"
      : tone === "cost"
        ? "text-[#8F5A16]"
        : "text-foreground";
  return (
    <div className="flex justify-between items-baseline gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={`sf-mono font-medium ${cls}`}>{value}</span>
    </div>
  );
}
